import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Switch } from '@/components/ui/Switch'
import { AvatarUpload } from '@/components/ui/AvatarUpload'
import { extractPalette } from '@/lib/extractPalette'

export interface ProductFormData {
  name: string
  header_name: string
  primary_color: string
  accent_color: string
  header_bg_color: string
  header_text_color: string
  active: boolean
}

// Fallbacks mirror the server's BRAND_DEFAULTS (Product#apply_brand_defaults) so
// the create form's swatches show what will actually be saved, not black.
const COLOR_FIELDS: { key: keyof ProductFormData; label: string; fallback: string }[] = [
  { key: 'primary_color', label: 'Primary', fallback: '#10b981' },
  { key: 'accent_color', label: 'Accent', fallback: '#10b981' },
  { key: 'header_bg_color', label: 'Header background', fallback: '#ffffff' },
  { key: 'header_text_color', label: 'Header text', fallback: '#18181b' },
]

interface ProductBrandEditorProps {
  formData: ProductFormData
  onFormChange: (data: ProductFormData) => void
  onSubmit: () => void
  onCancel: () => void
  isEditing: boolean
  logoUrl?: string | null
  onLogoUpload?: (file: File) => void
}

export function ProductBrandEditor({
  formData,
  onFormChange,
  onSubmit,
  onCancel,
  isEditing,
  logoUrl,
  onLogoUpload,
}: ProductBrandEditorProps) {
  const [suggestions, setSuggestions] = useState<string[]>([])

  const update = (key: keyof ProductFormData, value: string | boolean) =>
    onFormChange({ ...formData, [key]: value })

  const handleLogo = async (file: File) => {
    onLogoUpload?.(file)
    if (!formData.primary_color) setSuggestions(await extractPalette(file))
  }

  return (
    <div className="rounded-lg border border-primary/40 bg-muted/30 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">
          {isEditing ? `Editing ${formData.name || 'product'}` : 'New product'}
        </h4>
        <Button variant="ghost" size="sm" onClick={onCancel} className="cursor-pointer" aria-label="Close editor">
          <X className="size-4" />
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="product-name">Name *</Label>
          <Input
            id="product-name"
            value={formData.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="e.g. Acme Cloud"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="product-header-name">Header name</Label>
          <Input
            id="product-header-name"
            value={formData.header_name}
            onChange={(e) => update('header_name', e.target.value)}
            placeholder="Shown in widget/help header (defaults to name)"
          />
        </div>
      </div>

      {isEditing && onLogoUpload && (
        <div className="space-y-2">
          <Label>Logo</Label>
          <AvatarUpload
            currentUrl={logoUrl}
            initials={formData.name.slice(0, 2).toUpperCase()}
            onUpload={handleLogo}
            label="Upload logo"
          />
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Suggested from your logo</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((hex) => (
              <button
                key={hex}
                type="button"
                onClick={() => update('primary_color', hex)}
                className="size-7 rounded-full border border-border cursor-pointer"
                style={{ backgroundColor: hex }}
                title={hex}
                aria-label={`Use ${hex} as primary colour`}
              />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {COLOR_FIELDS.map(({ key, label, fallback }) => (
          <div key={key} className="space-y-1">
            <Label htmlFor={`product-${key}`}>{label}</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                id={`product-${key}`}
                value={(formData[key] as string) || fallback}
                onChange={(e) => update(key, e.target.value)}
                className="h-9 w-10 shrink-0 rounded border border-border cursor-pointer"
              />
              <Input
                value={(formData[key] as string) ?? ''}
                onChange={(e) => update(key, e.target.value)}
                className="font-mono"
                placeholder="#10B981"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-border pt-3">
        <div className="flex items-center gap-2">
          <Switch
            id="product-active"
            checked={formData.active}
            onCheckedChange={(checked) => update('active', checked)}
          />
          <Label htmlFor="product-active">Active</Label>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="cursor-pointer">
            Cancel
          </Button>
          <Button onClick={onSubmit} className="cursor-pointer">
            {isEditing ? 'Save changes' : 'Create product'}
          </Button>
        </div>
      </div>
    </div>
  )
}
